































































































import Vue from 'vue';
import { mapActions } from 'vuex';
import { merge, includes, isArray } from 'lodash';

import { BForm, BFormGroup, BFormSelect, VBTooltip } from 'bootstrap-vue';
import { SamlSettings } from '@/pages/settings/authentication/types';

import AdvanceSwitch from './AdvanceSwitch.vue';

export default Vue.extend({
  name: 'AdvanceSettingsTab',
  components: {
    BForm,
    BFormGroup,
    BFormSelect,
    AdvanceSwitch,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  props: {
    samlSettings: {
      type: Object as () => SamlSettings,
      default: {},
      required: true,
    },
  },
  computed: {
    requestedAuthnContextComparison: {
      get() {
        const value = this.samlSettings.advance.security['requestedAuthnContextComparison'];
        return isArray(value) ? value : [value];
      },
      set(value: string[]) {
        // If value is "exact" then should not be an array, because the library wants it that way
        const value_ = includes(value, 'exact') ? 'exact' : value;
        this.$emit(
          'update-saml-settings',
          merge({}, this.samlSettings, { advance: { security: { requestedAuthnContextComparison: value_ } } })
        );
      },
    },
    spNameIdFormat: {
      get() {
        return this.samlSettings.spNameIdFormat;
      },
      set(spNameIdFormat) {
        this.$emit('update-saml-settings', { ...this.samlSettings, spNameIdFormat });
      },
    },
    switches(): object[] {
      return [
        {
          model: 'nameIdEncrypted',
          label: 'Enable encrypted name ID',
          tooltip: 'Indicates that the name ID of the logout request sent by this SP will be encrypted.',
        },
        {
          model: 'authnRequestsSigned',
          label: 'Enable authentication request signed',
          tooltip: 'Indicates whether the authorization request messages sent by this SP will be signed',
        },
        {
          model: 'logoutRequestSigned',
          label: 'Enable single logout request',
          tooltip: 'Indicates whether the logout request messages sent by this SP will be signed.',
        },
        {
          model: 'logoutResponseSigned',
          label: 'Enable logout response signed',
          tooltip: 'Indicates whether the logout response messages sent by this SP will be signed.',
        },
        {
          model: 'signMetadata',
          label: 'Enable sign metadata',
          tooltip: 'Sign the metadata false or true (use SP certs)',
        },
        {
          model: 'wantMessagesSigned',
          label: 'Want messages signed',
          tooltip:
            'Indicates a requirement for the response, logout request and logout response elements received by this SP to be signed.',
        },
        {
          model: 'wantAssertionsSigned',
          label: 'Want assertions signed',
          tooltip:
            'Indicates a requirement for the assertion elements received by this SP to be signed. The Metadata of the SP will offer this info.',
        },
        {
          model: 'wantAssertionsEncrypted',
          label: 'Want assertions encrypted',
          tooltip: 'Indicates a requirement for the assertion elements received by this SP to be encrypted.',
        },
        {
          model: 'wantNameId',
          label: 'Want name ID',
          tooltip:
            'Indicates a requirement for the Name ID element on the SAMLResponse received by this SP to be present.',
        },
        {
          model: 'wantNameIdEncrypted',
          label: 'Want name ID encrypted',
          tooltip: 'Indicates a requirement for the Name ID received by this SP to be encrypted.',
        },
        {
          model: 'wantAttributeStatement',
          label: 'Want attribute statement',
          tooltip: 'Indicates a requirement for the AttributeStatement element.',
        },
      ];
    },
    requestedAuthnContextComparisonOptions(): object[] {
      return [
        {
          value: 'exact',
          text: 'exact [default]',
        },
        {
          value: 'urn:oasis:names:tc:SAML:2.0:ac:classes:PasswordProtectedTransport',
          text: 'Password Protected Transport',
        },
        {
          value: 'urn:oasis:names:tc:SAML:2.0:ac:classes:Password',
          text: 'Password',
        },
        {
          value: 'urn:oasis:names:tc:SAML:2.0:ac:classes:X509',
          text: 'X509',
        },
      ];
    },
    spNameIdFormatOptions(): object[] {
      return [
        {
          value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
          text: 'Unspecified [default]',
        },
        {
          value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
          text: 'Email Address',
        },
        {
          value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
          text: 'Persistent',
        },
        {
          value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient',
          text: 'Transient',
        },
      ];
    },
    signatureAlgorithmOptions(): object[] {
      return [
        {
          value: 'http://www.w3.org/2000/09/xmldsig#rsa-sha1',
          text: 'RSA-SHA1',
        },
        {
          value: 'http://www.w3.org/2000/09/xmldsig#dsa-sha1',
          text: 'DSA-SHA1',
        },
        {
          value: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha256',
          text: 'RSA-SHA256 [Default]',
        },
        {
          value: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha384',
          text: 'RSA-SHA384',
        },
        {
          value: 'http://www.w3.org/2001/04/xmldsig-more#rsa-sha512',
          text: 'RSA-SHA512',
        },
      ];
    },
    digestAlgorithmOptions(): object[] {
      return [
        {
          value: 'http://www.w3.org/2000/09/xmldsig#sha1',
          text: 'SHA1',
        },
        {
          value: 'http://www.w3.org/2001/04/xmlenc#sha256',
          text: 'SHA256 [Default]',
        },
        {
          value: 'http://www.w3.org/2001/04/xmldsig-more#sha384',
          text: 'SHA384',
        },
        {
          value: 'http://www.w3.org/2001/04/xmlenc#sha512',
          text: 'SHA512',
        },
      ];
    },
  },
  async created() {
    this.setPageTitle({ title: 'SAML Authentication' });
  },
  methods: {
    ...mapActions(['setPageTitle']),
    updateSecurity(propName: string, value: boolean | string) {
      this.$emit(
        'update-saml-settings',
        merge({}, this.samlSettings, { advance: { security: { [propName]: value } } })
      );
    },
  },
});
